import { memo, useMemo, useState } from "react";
import ImageLightbox from "./ImageLightbox";
import { imageUrl, imageUrls } from "../lib/dataClient";
import {
  haversineDistanceM,
  walkMinutes,
  carMinutes,
  formatDistance,
  formatDuration,
  walkDurationText,
  carDurationText,
} from "../lib/distance";

/**
 * 음식점 상세 — CafeDetail 패턴과 동일.
 *
 * Above-the-fold:
 *   [chip(Restaurant) + 이름 + 동네/카테고리]
 *   [📍 내 위치 거리]
 *   [💬 사장님 한마디 박스 ← 음식점 페이지 핵심]
 *
 * Below-the-fold:
 *   [정보 카드: 주소, 전화, 카페로부터 거리]
 *   [네이버 지도]
 */
export default memo(function PlaceDetail({ place, fromCafe, userLocation }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const rec = useMemo(
    () => fromCafe?.recommends?.find((r) => r.placeId === place.id),
    [fromCafe, place.id]
  );
  const menuHighlight = rec?.menuHighlight;
  const note = rec?.note;

  const distFromCafe =
    fromCafe && fromCafe.lat != null && place.lat != null
      ? haversineDistanceM(fromCafe.lat, fromCafe.lng, place.lat, place.lng)
      : null;
  const distFromUser =
    userLocation && place.lat != null
      ? haversineDistanceM(
          userLocation.lat,
          userLocation.lng,
          place.lat,
          place.lng
        )
      : null;

  const category = place.naverCategory?.split(">").pop()?.trim();

  return (
    <div className="cf-cafedetail">
      {/* === Above-the-fold === */}

      <div className="cf-cafedetail__head">
        <div className="cf-cafedetail__chip cf-cafedetail__chip--place">
          <span className="cf-cafedetail__chip-dot" />
          Restaurant
        </div>
        <h2 className="cf-cafedetail__name">{place.name}</h2>
        {(place.neighborhood || category) && (
          <div className="cf-cafedetail__head-sub">
            {place.neighborhood && <span>{place.neighborhood}</span>}
            {place.neighborhood && category && (
              <span className="cf-cafedetail__head-dot">·</span>
            )}
            {category && <span>{category}</span>}
          </div>
        )}
      </div>

      {distFromUser != null && (
        <div className="cf-cafedetail__quick-dist">
          <span className="cf-cafedetail__quick-dist-icon">📍</span>
          <span className="cf-cafedetail__quick-dist-text">
            내 위치에서 <strong>{formatDistance(distFromUser)}</strong>
            <span className="cf-cafedetail__quick-dist-sub">
              {" · "}🚶 {walkDurationText(distFromUser)} · 🚗{" "}
              {carDurationText(distFromUser)}
            </span>
          </span>
        </div>
      )}

      {Array.isArray(place.images) && place.images.length > 0 && (
        <div className="cf-cafedetail__thumbs">
          {place.images.map((src, idx) => (
            <div
              className="cf-cafedetail__thumb"
              key={idx}
              onClick={() => setLightboxIndex(idx)}
              role="button"
              tabIndex={0}
            >
              <img
                src={imageUrl(src)}
                alt={`${place.name} ${idx + 1}`}
                className="cf-cafedetail__thumb-img"
              />
            </div>
          ))}
        </div>
      )}

      {/* 사장님 한마디 박스 — 음식점 페이지의 핵심 */}
      {fromCafe && (menuHighlight || note) && (
        <div className="cf-quote">
          <div className="cf-quote__icon">💬</div>
          <div className="cf-quote__body">
            <div className="cf-quote__label">
              {fromCafe.name} 사장님의 한마디
            </div>
            {note ? (
              <p className="cf-quote__text">"{note}"</p>
            ) : (
              <p className="cf-quote__text">
                "{menuHighlight} 한번 드셔보세요"
              </p>
            )}
            {menuHighlight && note && (
              <div className="cf-quote__menu">
                <span className="cf-quote__menu-icon">⭐</span>
                추천 메뉴 · {menuHighlight}
              </div>
            )}
          </div>
        </div>
      )}

      {/* === Below-the-fold === */}

      <div className="cf-cafedetail__infobox">
        {place.roadAddress && (
          <div className="cf-cafedetail__inforow">
            <span className="cf-cafedetail__info-icon">📍</span>
            <span className="cf-cafedetail__info-text">
              {place.roadAddress}
            </span>
          </div>
        )}
        {place.naverTelephone && (
          <div className="cf-cafedetail__inforow">
            <span className="cf-cafedetail__info-icon">📞</span>
            <span className="cf-cafedetail__info-text">
              {place.naverTelephone}
            </span>
          </div>
        )}

        {(rec?.routing || distFromCafe != null) && fromCafe && (
          <div className="cf-cafedetail__inforow cf-cafedetail__inforow--dist">
            <span className="cf-cafedetail__info-icon">☕</span>
            <span className="cf-cafedetail__info-text">
              <strong>{fromCafe.name}</strong>에서{" "}
              <strong>
                {formatDistance(rec?.routing?.drivingDistance ?? distFromCafe)}
              </strong>
              <div className="cf-cafedetail__info-sub">
                {rec?.routing ? (() => {
                  const walkSec = rec.routing.walking?.duration ?? rec.routing.walkingDurationEst ?? 0;
                  const walkingDist = rec.routing.walking?.distance ?? rec.routing.drivingDistance;
                  const isAdjacent = walkingDist < 200;
                  if (isAdjacent) {
                    return (
                      <span>
                        🚶 걸어서 {formatDuration(walkSec)} · {formatDistance(walkingDist)}
                      </span>
                    );
                  }
                  return (
                    <span>
                      🚗 차 {formatDuration(rec.routing.drivingDuration)}
                      {" · "}
                      🚶 도보 {formatDuration(walkSec)}
                    </span>
                  );
                })() : (
                  <span>🚶 도보 {walkDurationText(distFromCafe)}</span>
                )}
              </div>
            </span>
          </div>
        )}
      </div>

      {place.naverMapUrl && (
        <div className="cf-cafedetail__actions">
          <a
            className="cf-cafedetail__action-btn"
            href={place.naverMapUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            네이버 지도에서 보기 ↗
          </a>
        </div>
      )}

      {Array.isArray(place.images) && place.images.length > 0 && (
        <ImageLightbox
          images={imageUrls(place.images)}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
          alt={place.name}
        />
      )}
    </div>
  );
});
